import { Button, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { MouseEvent, useCallback, useEffect, useState } from 'react'
import { Icons, InformationModal } from 'utoniq-ui'

import {
  Artist,
  Bank,
  Business,
  ConsoleRevenueQuery,
  Maybe,
  TransactionsQuery,
} from '@/clients/utoniq-core/schema'
import { ArtistPaymentType } from '@/clients/utoniq-core/schema'
import {
  ArtistHeaderItemsLeft,
  ArtistHeaderItemsRight,
  Head,
} from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { Tabs } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

import { RevenueCell } from './RevenueCell'
import { RevenueHistoryListCell } from './RevenueHistoryCell'

export * from './fragments'

export type State = {
  artistId: string
  yearOfRevenues: number[]
  loading: boolean
  loadingHistories: boolean
  hasNextPageHistories: boolean
  histories: Maybe<TransactionsQuery['transactions']>
  salesReport: Maybe<ConsoleRevenueQuery['salesReport']>
  transactions: Maybe<ConsoleRevenueQuery['transactions']>
  artist:
    | Maybe<
        Pick<
          Artist,
          | 'id'
          | 'paymentType'
          | 'status'
          | 'createdAt'
          | 'hasArrangingDeliveryItems'
        > & {
          bank?: Maybe<Pick<Bank, 'id'>> | undefined
          business?:
            | Maybe<Pick<Business, 'firstNameKana' | 'firstNameKanji'>>
            | undefined
        }
      >
    | undefined
  openingPayout: boolean
}

export type Action = {
  onApply: () => Promise<void>
  onFetchMore: () => Promise<void>
  onChangeTab: (values: {
    type: 'salesManagement' | 'revenueHistory'
  }) => Promise<void>
  onExportCSV: (year: number) => Promise<void>
}

const tabs: Array<'salesManagement' | 'revenueHistory'> = [
  'salesManagement',
  'revenueHistory',
]

export const ConsoleRevenueTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onApply,
  onChangeTab,
  onExportCSV,
}) => {
  const { t } = useTranslation('consoleRevenue')
  const { routerNames } = useRouter()
  const { showError, showMessage } = useMessage()
  const [tab, setTab] = useState<number>(0)
  const [exportingRevenueTransactions, setExportingRevenueTransactions] =
    useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleChangeTab = useCallback(
    async (newValue: number) => {
      setTab(newValue)
      await onChangeTab({
        type: newValue ? 'revenueHistory' : 'salesManagement',
      })
    },
    [onChangeTab]
  )

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleRevenue} />
      <ConsoleBaseLayout
        hasConsoleFooter
        header={{
          left: <ArtistHeaderItemsLeft />,
          right: <ArtistHeaderItemsRight artistId={state.artistId} />,
        }}
      >
        <Stack
          sx={{ px: 5, py: 3, mt: 2 }}
          direction="row"
          justifyContent="space-between"
        >
          <Typography variant="h3">{t('title')}</Typography>
          {tab === 1 && !!state.histories?.edges.length && (
            <>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 1,
                  borderColor: 'primary.main',
                  minWidth: 148,
                  '&.Mui-disabled': {
                    border: 'none',
                  },
                }}
                onClick={handleClick}
                disabled={
                  !state.yearOfRevenues.length || exportingRevenueTransactions
                }
                loading={exportingRevenueTransactions}
                loadingPosition="center"
              >
                {exportingRevenueTransactions ? (
                  <Typography
                    variant="subtitle1"
                    color="text.disabled"
                    sx={{ mb: '-2px' }}
                  >
                    {t('downloading')}
                  </Typography>
                ) : (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      sx={{ mb: '-2px' }}
                    >
                      {t('exportCsv')}
                    </Typography>
                    <Icons.DownArrow fontSize="xSmall" color="primary" />
                  </Stack>
                )}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                defaultValue=""
                autoFocus={false}
              >
                {state.yearOfRevenues.map(year => (
                  <MenuItem
                    onClick={async () => {
                      handleClose()
                      setExportingRevenueTransactions(true)
                      await onExportCSV(year)
                        .then(() =>
                          showMessage(t('downloadCsvSuccess'), 'success')
                        )
                        .catch(error => showError(error))
                      setExportingRevenueTransactions(false)
                    }}
                    value={year}
                    key={year}
                    sx={{ width: 160 }}
                  >
                    {t('earningsInfo', { year })}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Stack>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          disableAnimateTransitions
          labels={tabs.map(tab => t(`${tab}`))}
        >
          <RevenueCell
            loading={state.loading}
            artist={state.artist}
            salesReport={state.salesReport}
            transactions={state.transactions}
            onApply={onApply}
            openingPayout={state.openingPayout}
          />
          <RevenueHistoryListCell
            loading={state.loadingHistories}
            hasNextPage={state.hasNextPageHistories}
            onFetchMore={onFetchMore}
            histories={state.histories}
          />
        </Tabs>
        {state.artist &&
          state.artist?.paymentType !== ArtistPaymentType.Billing &&
          !state.loading && <BuiznessModal artist={state.artist} />}
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

/**
 * ビジネス情報・銀行口座情報を登録していない場合に表示するモーダル
 * https://zpl.io/EmZJpwy
 */
const BuiznessModal = ({ artist }: { artist: State['artist'] }) => {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation('consoleRevenue')
  const { routerPages } = useRouter()

  useEffect(() => {
    setOpen(artist?.status !== 'active' || !artist.bank || !artist.business)
  }, [artist?.status, artist?.bank, artist?.business])

  const titleText =
    !artist?.bank && !artist?.business
      ? 'businessAndBank'
      : !artist?.bank
      ? 'bank'
      : 'business'

  return (
    <InformationModal
      open={open}
      onClose={() => setOpen(false)}
      title={t(`${titleText}ModalTitle`)}
      content={t(`${titleText}ModalContent`)}
      cancelText={t('cancel')}
      buttons={
        <Stack spacing={5}>
          {!artist?.bank && (
            <Button
              size="large"
              color="primary"
              href={routerPages.ConsoleBankEdit}
              sx={{ minWidth: 212 }}
            >
              {t('registerBankButton')}
            </Button>
          )}
          {!artist?.business && (
            <Button
              size="large"
              color="primary"
              href={routerPages.ConsoleBusinessEdit}
              sx={{ minWidth: 212 }}
            >
              {t('registerBusinessButton')}
            </Button>
          )}
        </Stack>
      }
    />
  )
}
